import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useLoading } from "app/custom-hooks/loadingHook";
import { i18nFilter } from "app/filters/i18nFilter";
import DurationService from "app/services/DurationService";
import SegmentUserFlowApiService from "app/services/segmentUserFlow/SegmentUserFlowApiService";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import {
    BoxContainer,
    BuyerJourneyBoxContainer,
    ContentContainer,
    StyledHeaderTitle,
    TitleContainer,
} from "./StyledComponents";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { useSelector } from "react-redux";
import { BannerContainer, FlexRowAlignCenter } from "app/pages/segments/geography/StyledComponents";
import { ChipInteractionFlowPager } from "@similarweb/ui-components";
import SegmentUserFlowBucketTable from "app/pages/segment-user-flow/buyer-journey/SegmentUserFlowBucketTable";
import { FlowChartContainer } from "app/pages/segment-user-flow/buyer-journey/FlowChartContainer";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import type { ICustomSegment } from "app/services/segments/segmentsWizardServiceTypes";
import { Interactions } from "./Interactions";
import { UserFlowPagerNotificationBanner } from "app/pages/segment-user-flow/buyer-journey/UserFlowPagerNotificationBanner";
import { isEmpty } from "lodash";
import { allTrackers } from "app/services/track/track";

export interface IInteraction {
    interactionIndex: number;
    interactionName: string;
    interactionId: string;
}

const DROPOFF_SERVER_LABEL = "";
const DROPOFF_CLIENT_LABEL = "DropOff";
const SEGMENT_CLIENT_LABEL = "Segment";
export const SegmentUserFlowContainer: React.FunctionComponent = (props: any) => {
    const { customSegmentsMeta } = useSelector((state) => {
        const {
            segmentsModule: { customSegmentsMeta },
        } = state;
        return { customSegmentsMeta };
    });
    const segmentUserFlowApiService = useMemo(() => {
        return new SegmentUserFlowApiService();
    }, []);
    const segmentsApiService = useMemo(() => {
        return new SegmentsApiService();
    }, []);
    const { state, params } = useCurrentStateAndParams();
    const { mode } = params;
    const durationObject = useMemo(() => {
        const { duration } = params;
        return DurationService.getDurationData(duration, undefined);
    }, [params]);
    const [selectedBucket, setSelectedBucket] = useState(Interactions.B3);
    const [visiblePreInteractions, setVisiblePreInteractions] = useState([Interactions.B3]);
    const [visiblePostInteractions, setVisiblePostInteractions] = useState([]);
    const onAddPreInteractionButtonClick = useCallback(
        (interactionIndex: number) => {
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `segment/analysis/userflow/interactionPager/add/interaction/${interactionIndex || ""}`,
            );
            setVisiblePreInteractions([Interactions[`B${interactionIndex}`], ...visiblePreInteractions]);
        },
        [visiblePreInteractions],
    );
    const onAddPostInteractionButtonClick = useCallback(
        (interactionIndex: number) => {
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `segment/analysis/userflow/interactionPager/add/interaction/${interactionIndex || ""}`,
            );
            setVisiblePostInteractions([...visiblePostInteractions, Interactions[`B${interactionIndex}`]]);
        },
        [visiblePostInteractions],
    );
    const onSetSelectedInteractionButtonClick = useCallback(
        (interaction: IInteraction) => {
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `segment/analysis/userflow/interactionPager/select/interaction/${interaction.interactionIndex || ""}`,
            );
            setSelectedBucket(Interactions[interaction.interactionId]);
        },
        [visiblePostInteractions],
    );
    const onRemovePostInteractionButtonClick = useCallback(
        (interactionIndex: number) => {
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `segment/analysis/userflow/interactionPager/remove/interaction/${interactionIndex || ""}`,
            );
            setVisiblePostInteractions(
                visiblePostInteractions.filter(
                    (interactionItem) => interactionItem.interactionIndex < interactionIndex,
                ),
            );
            if (selectedBucket.interactionIndex >= interactionIndex) {
                setSelectedBucket(Interactions.B3);
            }
        },
        [visiblePostInteractions, selectedBucket],
    );
    const onRemovePreInteractionButtonClick = useCallback(
        (interactionIndex: number) => {
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `segment/analysis/userflow/interactionPager/remove/interaction/${interactionIndex || ""}`,
            );
            setVisiblePreInteractions(
                visiblePreInteractions.filter((interactionItem) => interactionItem.interactionIndex > interactionIndex),
            );
            if (selectedBucket.interactionIndex <= interactionIndex) {
                setSelectedBucket(Interactions.B3);
            }
        },
        [visiblePreInteractions, selectedBucket],
    );

    const apiParams = proNavigator.getApiParams(params);
    const [buyerJourneyData, buyerJourneyDataOps] = useLoading();
    const isBuyerJourneyLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(buyerJourneyData.state);

    const [customSegmentRulesData, customSegmentRulesDataOps] = useLoading();
    const isCustomSegmentRulesLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(
        customSegmentRulesData.state,
    );
    React.useEffect(() => {
        customSegmentRulesDataOps.load(() => {
            const { id } = params;
            return segmentsApiService.getSegmentRules(id);
        });
    }, [params]);

    const transformTablePairsBucketData = (selectedBucket, buyerJourneyData) => {
        //TODO: Remove reduce if we decide we want to filter result set in BE
        return buyerJourneyData?.data?.GroupedByBucket?.[selectedBucket.interactionId]?.reduce(
            (dropFreeJourney, dataItem) => {
                if (dataItem.To !== DROPOFF_SERVER_LABEL && dataItem.To !== dataItem.From) {
                    dropFreeJourney.push({
                        From: dataItem.From,
                        To: dataItem.To,
                        Visits: dataItem.Value.TotalPairVisits,
                        Share: dataItem.Value.TotalPairVisits / dataItem.Value.TotalBucketVisits,
                    });
                }
                return dropFreeJourney;
            },
            [],
        );
    };

    const labelPageAsSegmentOrDropOff = (page: string, segmentMetaData: ICustomSegment) => {
        return SegmentsUtils.matchUrlByRules(page, segmentMetaData.rules, segmentMetaData.domain)
            ? SEGMENT_CLIENT_LABEL
            : page === DROPOFF_SERVER_LABEL
              ? DROPOFF_CLIENT_LABEL
              : page;
    };

    const transformGraphPopularPairsBucketData = (buyerJourneyData, segmentMetaData: ICustomSegment) => {
        const TopPairsDic = {};
        [...visiblePreInteractions, ...visiblePostInteractions].map(({ interactionId }) => {
            const interactionObj = Interactions[interactionId];
            let bucketItemsAddedToGraphRepresentation = 1;
            buyerJourneyData?.data?.GroupedByBucket?.[interactionId]?.map((dataItem) => {
                if (bucketItemsAddedToGraphRepresentation > interactionObj.maxPairs) {
                    return false;
                }
                //Remove DropOFF on Client side
                if (dataItem.To === DROPOFF_SERVER_LABEL) {
                    return;
                }
                const labeledUrlFrom = labelPageAsSegmentOrDropOff(dataItem.From, segmentMetaData);
                const labeledUrlTo = labelPageAsSegmentOrDropOff(dataItem.To, segmentMetaData);
                //Remove circular Traffic // remove self referrals
                if (TopPairsDic[`${labeledUrlTo}_${labeledUrlFrom}`] || labeledUrlFrom === labeledUrlTo) {
                    return;
                }
                const currentPath = `${labeledUrlFrom}_${interactionObj.interactionIndex}_${labeledUrlTo}_${
                    interactionObj.interactionIndex + 1
                }`;
                if (TopPairsDic[currentPath]) {
                    TopPairsDic[currentPath] = [
                        labeledUrlFrom + `_${interactionObj.interactionIndex}`,
                        labeledUrlTo + `_${interactionObj.interactionIndex + 1}`,
                        TopPairsDic[currentPath][2] + dataItem.Value.TotalPairVisits,
                        TopPairsDic[currentPath][3] + dataItem.Value.TotalPairVisits / dataItem.Value.TotalBucketVisits,
                        interactionObj.interactionIndex,
                    ];
                    bucketItemsAddedToGraphRepresentation++;
                } else {
                    TopPairsDic[currentPath] = [
                        labeledUrlFrom + `_${interactionObj.interactionIndex}`,
                        labeledUrlTo + `_${interactionObj.interactionIndex + 1}`,
                        dataItem.Value.TotalPairVisits,
                        dataItem.Value.TotalPairVisits / dataItem.Value.TotalBucketVisits,
                        interactionObj.interactionIndex,
                    ];
                    bucketItemsAddedToGraphRepresentation++;
                }
            });
        });
        return Object.values(TopPairsDic).sort((a, b) => {
            return a[2] < b[2] ? 1 : -1;
        });
    };

    const tableData = useMemo(() => {
        if (!buyerJourneyData || isBuyerJourneyLoading || buyerJourneyData.state === "error") {
            return undefined;
        }
        return transformTablePairsBucketData(selectedBucket, buyerJourneyData);
    }, [selectedBucket, buyerJourneyData]);

    const graphData = useMemo(() => {
        if (
            !buyerJourneyData ||
            isBuyerJourneyLoading ||
            isCustomSegmentRulesLoading ||
            buyerJourneyData.state === "error"
        ) {
            return undefined;
        }
        return transformGraphPopularPairsBucketData(buyerJourneyData, customSegmentRulesData.data);
    }, [selectedBucket, buyerJourneyData, visiblePreInteractions, visiblePostInteractions]);

    const renderNoData = () => {
        return (
            <TableNoData
                icon="no-data"
                messageTitle={i18nFilter()("global.nodata.notavilable")}
            />
        );
    };
    const renderGroupComparisonNotSupported = () => {
        return (
            <TableNoData
                icon="no-data"
                messageTitle={i18nFilter()("segment.analysis.user.flow.comparison.not.supported.title")}
                messageSubtitle={i18nFilter()("segment.analysis.user.flow.comparison.not.supported.subtitle")}
            />
        );
    };
    const segmentMetaData = useMemo(() => {
        return SegmentsUtils.getSegmentById({ segments: customSegmentsMeta.AccountSegments }, params.id);
    }, [params]);

    React.useEffect(() => {
        buyerJourneyDataOps.load(() => {
            const {
                country,
                from,
                to,
                timeGranularity = "Monthly",
                webSource = "Desktop",
                includeSubDomains = true,
                isWindow,
            } = apiParams;
            return segmentUserFlowApiService.getCustomSegmentUserFlowData({
                country,
                from,
                to,
                timeGranularity,
                webSource,
                includeSubDomains,
                segmentId: params.id,
                isWindow,
                keys: null,
                lastUpdated: segmentMetaData?.lastUpdated,
            });
        });
    }, [params]);
    const subtitleFilters = React.useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: durationObject.raw.from.valueOf(),
                    to: durationObject.raw.to.valueOf(),
                    useRangeDisplay:
                        params.duration !== "28d" ||
                        durationObject.raw.from.format("YYYY-MM") !== durationObject.raw.to.format("YYYY-MM"),
                },
            },
            {
                filter: "webSource",
                value: "Desktop",
            },
        ],
        [params.country, durationObject],
    );

    const isNoData = mode === "group" || (isEmpty(graphData) && isEmpty(tableData));

    return (
        <BuyerJourneyBoxContainer>
            <BoxContainer
                data-automation-user-journey={true}
                isEmptyState={isNoData}
            >
                <TitleContainer>
                    <FlexColumn>
                        <StyledHeaderTitle>
                            <BoxTitle tooltip={i18nFilter()("buyer.journey.title.tooltip")}>
                                {i18nFilter()("buyer.journey.title")}
                            </BoxTitle>
                        </StyledHeaderTitle>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                    </FlexColumn>
                </TitleContainer>
                {mode === "group" ? (
                    renderGroupComparisonNotSupported()
                ) : (!isBuyerJourneyLoading && !(buyerJourneyData && buyerJourneyData.data)) ||
                  buyerJourneyData.state === "error" ? (
                    renderNoData()
                ) : (
                    <ContentContainer>
                        <FlowChartContainer
                            isLoading={isBuyerJourneyLoading || isCustomSegmentRulesLoading}
                            graphData={graphData}
                            seriesName={customSegmentRulesData?.data?.segmentName}
                            interactionsMeta={Interactions}
                        />
                        <FlexRowAlignCenter>
                            <ChipInteractionFlowPager
                                selectedInteraction={selectedBucket}
                                preInteractions={[Interactions.B1, Interactions.B2, Interactions.B3]}
                                postInteractions={[Interactions.B4, Interactions.B5, Interactions.B6]}
                                visiblePreInteractions={visiblePreInteractions}
                                visiblePostInteractions={visiblePostInteractions}
                                defaultInteraction={Interactions.B3}
                                mainThemeItem={{
                                    mainThemeLabel: "Segment",
                                    iconName: "segment-share",
                                }}
                                onAddPreInteractionButtonClick={onAddPreInteractionButtonClick}
                                onAddPostInteractionButtonClick={onAddPostInteractionButtonClick}
                                onSetSelectedInteractionButtonClick={onSetSelectedInteractionButtonClick}
                                onRemovePostInteractionButtonClick={onRemovePostInteractionButtonClick}
                                onRemovePreInteractionButtonClick={onRemovePreInteractionButtonClick}
                                addPreInteractionButtonTooltipText={i18nFilter()(
                                    "segment.analysis.user.journey.flow.pager.add.pre.interaction.button",
                                )}
                                addPostInteractionButtonTooltipText={i18nFilter()(
                                    "segment.analysis.user.journey.flow.pager.add.post.interaction.button",
                                )}
                                selectButtonTooltipText={i18nFilter()(
                                    "segment.analysis.user.journey.flow.pager.select.interaction.button",
                                )}
                                selectButtonSelectedTooltipText={i18nFilter()(
                                    "segment.analysis.user.journey.flow.pager.select.interaction.button.selected",
                                    { name: selectedBucket.interactionName },
                                )}
                            />
                        </FlexRowAlignCenter>
                        <BannerContainer>
                            <UserFlowPagerNotificationBanner interaction={selectedBucket} />
                        </BannerContainer>
                        {isBuyerJourneyLoading || buyerJourneyData.data ? (
                            <SegmentUserFlowBucketTable
                                tableData={tableData}
                                isLoading={isBuyerJourneyLoading}
                                selectedInteraction={selectedBucket}
                            />
                        ) : null}
                    </ContentContainer>
                )}
            </BoxContainer>
        </BuyerJourneyBoxContainer>
    );
};
